// src/context/WishlistContext.js
import React, { createContext, useState, useEffect } from 'react';

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchWishlist = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setWishlistIds(new Set());
      setHasFetched(false);
      return;
    }

    // Prevent multiple simultaneous fetches
    if (loading) return;

    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        const ids = new Set(data.products?.map(p => p._id) || []);
        setWishlistIds(ids);
        setHasFetched(true);
      } else {
        if (res.status === 401) {
          localStorage.removeItem('token');
        }
        setWishlistIds(new Set());
      }
    } catch (err) {
      console.error('Wishlist fetch error:', err);
      setWishlistIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch once on mount if user is logged in
    const token = localStorage.getItem('token');
    if (token && !hasFetched) {
      fetchWishlist();
    }
  }, []);

  const isInWishlist = (productId) => {
    return wishlistIds.has(productId);
  };

  const addToWishlist = (productId) => {
    setWishlistIds(prev => {
      const newSet = new Set(prev);
      newSet.add(productId);
      return newSet;
    });
  };

  const removeFromWishlist = (productId) => {
    setWishlistIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(productId);
      return newSet;
    });
  };

  return (
    <WishlistContext.Provider value={{
      isInWishlist,
      addToWishlist,
      removeFromWishlist,
      refreshWishlist: fetchWishlist,
      loading
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
